import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import {
  palette,
  shadow,
  spacing,
  typography,
  layout,
} from '../../style-utils/theme';
import { between, touch } from '../../style-utils/media-queries';
import { LEGAL_DOCS, legalHref } from './legal-docs';

/* A row of small links to the policies and FAQ: Privacy · Terms · Cookies ·
   DPA · Security · FAQ. Each opens the in-app copy at /legal/<doc>.

   `onBrand` is the sign-in screen's version: a compact bar in the teal brand
   panel, under the logo. */

type Tone = 'default' | 'onBrand';

const Row = styled.nav<{ $tone: Tone }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${({ $tone }) =>
    $tone === 'onBrand' ? 'flex-start' : 'center'};
  column-gap: ${({ $tone }) => ($tone === 'onBrand' ? '12px' : spacing.sm)};
  font-family: ${typography.ui};
  font-size: ${({ $tone }) =>
    $tone === 'onBrand' ? typography.size.xxs : typography.size.xs}px;
  font-weight: ${typography.weight.medium};

  /* The panel's top corner is the LIGHT end of the brand gradient, where
     white text alone measures about 2.5:1. The row carries the ink scrim so
     the links clear 4.5:1 wherever the gradient puts them. */
  ${({ $tone }) =>
    $tone === 'onBrand'
      ? `
    padding: 0 ${spacing.sm};
    border-radius: 14px;
    background: ${palette.overlay};
  `
      : ''}

  & a {
    display: inline-flex;
    align-items: center;
    min-height: ${({ $tone }) =>
      $tone === 'onBrand' ? '28px' : layout.touchTarget};
    color: ${({ $tone }) =>
      $tone === 'onBrand' ? palette.onBrandStrong : palette.slate};
    text-decoration: none;
    white-space: nowrap;
  }

  & a:hover {
    color: ${({ $tone }) =>
      $tone === 'onBrand' ? palette.white : palette.lagoon};
    text-decoration: underline;
  }

  & a:focus-visible {
    outline: none;
    box-shadow: ${({ $tone }) =>
      $tone === 'onBrand' ? `0 0 0 2px ${palette.white}` : shadow.focus};
    border-radius: 4px;
  }

  /* The sign-in card's two-column range is where the brand panel is at its
     narrowest (about 280px of content). Tighter spacing keeps the bar to one
     line there instead of stranding the last link on a second. */
  ${({ $tone }) =>
    $tone === 'onBrand'
      ? `${between('md', 'lg')} {
    column-gap: 8px;
    padding: 0 10px;
  }`
      : ''}

  /* Small text is fine; a small TARGET is not. On a touch screen every link
     grows to the 44px floor. */
  ${touch} {
    & a {
      min-height: ${layout.touchTarget};
    }
  }
`;

export const LegalLinks: React.FC<{ tone?: Tone; className?: string }> = ({
  tone = 'default',
  className,
}) => (
  <Row $tone={tone} aria-label="Legal" className={className}>
    {LEGAL_DOCS.map(doc => (
      <Link key={doc.slug} to={legalHref(doc.slug)} title={doc.title}>
        {doc.short}
      </Link>
    ))}
  </Row>
);
